import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DialogService } from '../../../services/dialog.service';
import { DrupalRESTService } from '../../../services/drupal-rest.service';
import { MenuRESTService } from '../../../services/menu-rest.service';
import { AuthService } from '../../../services/auth.service';
import { StudentInquiryEntityComponent } from '../../forms/student-inquiry-entity/student-inquiry-entity.component';
import { EnrollmentEntityComponent } from '../../forms/enrollment-entity/enrollment-entity.component';
import { PaymentsEntityComponent } from '../../forms/payments-entity/payments-entity.component';
import { EventLessonEntityComponent } from '../../forms/event-lesson-entity/event-lesson-entity.component';
import { SignatureEnrollmentComponent } from '../../enrollment/signature-enrollment/signature-enrollment.component';
import { EnrollmentDropComponent } from '../../forms/enrollment-drop/enrollment-drop.component';
import { EnrollmentViewLessonComponent } from '../../forms/enrollment-view-lesson/enrollment-view-lesson.component';
import { ViewPaymentsFromEnrollmentComponent } from '../../forms/view-payments-from-enrollment/view-payments-from-enrollment.component';
import { ViewPaymentsDueFromEnrollmentComponent } from '../../forms/view-payments-due-from-enrollment/view-payments-due-from-enrollment.component';
import { StudentDashboardWrapperComponent } from '../../student/student-dashboard-wrapper/student-dashboard-wrapper.component';
import { PaymentReceiptComponent } from '../../payments/payment-receipt/payment-receipt.component';
import { EntityContextMenuDialogComponent, EntityContextMenuData, EntityContextMenuItem } from '../../utility/entity-context-menu-dialog/entity-context-menu-dialog.component';
import { Subject } from 'rxjs';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PermissionsService } from '../../../services/permissions.service';
import { LoggerService } from '../../../services/logger.service';

interface Command {
  id: string;
  label: string;
  icon: string;
  route?: string;
  handler?: () => void;
  keywords?: string[];
  type?: string;
  subtitle?: string;
  isEntity?: boolean;
  contact_id?: string;
  permission?: string;
  isVisible?: () => boolean;
}

interface EntitySearchResult {
  id: string;
  contact_id?: string;
  type: string;
  title: string;
  subtitle: string;
  icon: string;
  route: string;
}

@Component({
  selector: 'app-command-palette',
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.css']
})
export class CommandPaletteComponent implements OnInit, OnDestroy {
  searchQuery = '';
  selectedIndex = 0;
  isSearchingEntities = false;
  entityResults: Command[] = [];

  private searchSubject = new Subject<string>();
  private permissionSub?: Subscription;
  private studioSub?: Subscription;

  private staticCommands: Command[] = [
    {
      id: 'calendar',
      label: 'Go to Calendar (Day View)',
      icon: 'calendar_today',
      route: '/day-view',
      permission: 'calendar_view',
      keywords: ['calendar', 'day', 'appointments']
    },
    {
      id: 'add-inquiry',
      label: 'Add Inquiry',
      icon: 'person_add',
      handler: () => this.openNewInquiry(),
      permission: 'students_create',
      keywords: ['inquiry', 'lead', 'student', 'add']
    },
    {
      id: 'schedule-class',
      label: 'Schedule Class',
      icon: 'event',
      handler: () => this.openNewLesson(),
      permission: 'lessons_create',
      keywords: ['schedule', 'class', 'lesson', 'event']
    },
    {
      id: 'process-payment',
      label: 'Process Payment',
      icon: 'payment',
      handler: () => this.openPayment(),
      permission: 'payments_collect',
      keywords: ['payment', 'billing', 'invoice', 'process']
    },
    {
      id: 'new-enrollment',
      label: 'New Enrollment',
      icon: 'school',
      handler: () => this.openEnrollment(),
      permission: 'enrollments_create',
      keywords: ['enrollment', 'student', 'signup', 'register']
    },
    {
      id: 'view-reports',
      label: 'View Reports',
      icon: 'assessment',
      route: '/ami-reports',
      permission: 'reports_ami',
      keywords: ['reports', 'analytics', 'metrics', 'view']
    },
    {
      id: 'todays-schedule',
      label: 'Today\'s Schedule',
      icon: 'calendar_today',
      route: '/day-view',
      permission: 'calendar_view',
      keywords: ['schedule', 'today', 'appointments']
    },
    {
      id: 'week-view',
      label: 'Go to Week View',
      icon: 'view_week',
      route: '/week-view',
      permission: 'calendar_view',
      keywords: ['week', 'schedule', 'calendar']
    },
    {
      id: 'students',
      label: 'Go to Students',
      icon: 'people',
      route: '/students-list',
      permission: 'students_view',
      keywords: ['students', 'people', 'list']
    },
    {
      id: 'payments',
      label: 'Go to Payments',
      icon: 'payment',
      route: '/payment-list',
      permission: 'payments_view',
      keywords: ['payments', 'money', 'billing']
    },
    {
      id: 'reports',
      label: 'Go to Reports',
      icon: 'assessment',
      route: '/ami-reports',
      permission: 'reports_ami',
      keywords: ['reports', 'analytics', 'stats']
    },
    {
      id: 'settings',
      label: 'Go to Settings',
      icon: 'settings',
      route: '/configuration',
      isVisible: () => this.canAccessAny(['config_studio', 'staff_view', 'config_import', 'config_integrations']),
      keywords: ['settings', 'config', 'configuration', 'preferences']
    },
    {
      id: 'dashboard',
      label: 'Go to Dashboard',
      icon: 'dashboard',
      route: '/dashboard',
      keywords: ['dashboard', 'home', 'overview']
    },
    {
      id: 'enrollment',
      label: 'Go to Enrollments',
      icon: 'school',
      route: '/enrollment',
      permission: 'enrollments_view',
      keywords: ['enrollment', 'packages', 'sign up']
    },
    {
      id: 'packages',
      label: 'Go to Packages',
      icon: 'inventory_2',
      route: '/package-list',
      permission: 'packages_view',
      keywords: ['packages', 'products', 'offerings']
    }
  ];

  commands: Command[] = [];
  filteredCommands: Command[] = [];

  constructor(
    public dialogRef: MatDialogRef<CommandPaletteComponent>,
    private router: Router,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private drupalRESTService: DrupalRESTService,
    private menuRESTService: MenuRESTService,
    private authService: AuthService,
    private permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.rebuildCommands();

    // Setup debounced entity search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchEntities(query);
    });

    this.permissionSub = this.permissionsService.studioPermissionsLoaded.subscribe(() => {
      this.rebuildCommands();
    });
    this.studioSub = this.authService.selectedStudioChanged.subscribe(() => {
      this.rebuildCommands();
    });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    this.permissionSub?.unsubscribe();
    this.studioSub?.unsubscribe();
  }

  onSearchChange(): void {
    const query = this.searchQuery.toLowerCase().trim();

    // Filter static commands
    if (!query) {
      this.filteredCommands = [...this.commands];
      this.entityResults = [];
    } else {
      this.filteredCommands = this.commands.filter(cmd =>
        cmd.label.toLowerCase().includes(query) ||
        cmd.keywords?.some(keyword => keyword.includes(query))
      );

      // Trigger entity search if query is at least 2 characters
      if (query.length >= 2) {
        this.searchSubject.next(query);
      } else {
        this.entityResults = [];
      }
    }

    this.selectedIndex = 0;
  }

  private searchEntities(query: string): void {
    if (!query || query.length < 2) {
      this.entityResults = [];
      return;
    }

    const studioId = this.authService.selectedStudio?.id;
    if (!studioId) {
      LoggerService.warn('No studio selected, cannot search entities');
      this.entityResults = [];
      return;
    }

    this.isSearchingEntities = true;

    const params = [
      { parameter: 'q', value: query },
      { parameter: 'type', value: 'all' },
      { parameter: 'studio_id', value: studioId }
    ];

    this.drupalRESTService.httpGET('/api/command-palette/search', params).subscribe({
      next: (response: any) => {
        this.entityResults = (response.results || []).map((result: EntitySearchResult) => ({
          id: result.id,
          contact_id: result.contact_id,
          label: result.title,
          subtitle: result.subtitle,
          icon: result.icon,
          route: result.route,
          type: result.type,
          isEntity: true
        })).filter(result => this.canAccessEntityResult(result));
        this.isSearchingEntities = false;
      },
      error: (error) => {
        LoggerService.error('Entity search failed:', error);
        this.entityResults = [];
        this.isSearchingEntities = false;
      }
    });
  }

  get allFilteredCommands(): Command[] {
    return [...this.filteredCommands, ...this.entityResults];
  }

  executeCommand(command: Command): void {
    if (!this.isCommandVisible(command)) {
      return;
    }

    // Handle entity results differently based on type
    if (command.isEntity) {
      if (command.type === 'student' && command.contact_id) {
        // Open student dashboard dialog with contact ID
        this.dialogService.openDialog(StudentDashboardWrapperComponent, 'largerScreenDashboard', {
          data: {
            student_id: command.contact_id
          }
        });
        this.close();
        return;
      }

      if (command.type === 'payment') {
        // Show context menu for payment actions
        this.showPaymentContextMenu(command);
        return;
      }

      if (command.type === 'enrollment') {
        this.showEnrollmentContextMenu(command);
        return;
      }

      if (command.type === 'lesson') {
        // Open lesson edit dialog
        this.dialogService.openDialog(EventLessonEntityComponent, 'defaultWithData', {
          data: {
            EntityID: command.id,
            eckType: 'events',
            bundle: 'lessons',
            action: 'edit'
          }
        });
        this.close();
        return;
      }
    }

    // Handle standard commands
    if (command.handler) {
      command.handler();
    } else if (command.route) {
      this.router.navigate([command.route]);
    }
    this.close();
  }

  close(): void {
    this.dialogRef.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  @HostListener('document:keydown.arrowdown', ['$event'])
  onArrowDown(event: KeyboardEvent): void {
    event.preventDefault();
    const totalCommands = this.allFilteredCommands.length;
    if (this.selectedIndex < totalCommands - 1) {
      this.selectedIndex++;
      this.scrollToSelected();
    }
  }

  @HostListener('document:keydown.arrowup', ['$event'])
  onArrowUp(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.selectedIndex > 0) {
      this.selectedIndex--;
      this.scrollToSelected();
    }
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnter(event: KeyboardEvent): void {
    event.preventDefault();
    const allCommands = this.allFilteredCommands;
    if (allCommands.length > 0) {
      this.executeCommand(allCommands[this.selectedIndex]);
    }
  }

  private scrollToSelected(): void {
    setTimeout(() => {
      const element = document.querySelector('.command-item.selected');
      element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  private openNewInquiry(): void {
    this.dialogService.openDialog(StudentInquiryEntityComponent, 'defaultWithData', {
      data: {
        EntityID: null,
        eckType: 'student_accounts',
        bundle: 'student_account',
        action: 'create',
        fieldsData: {
          inquiry: true
        }
      }
    });
  }

  private openEnrollment(): void {
    this.dialogService.openDialog(EnrollmentEntityComponent, 'enrollmentFormWithData', {
      data: {
        EntityID: null,
        eckType: 'enrollment',
        bundle: 'enrollment',
        action: 'create'
      }
    });
  }

  private openPayment(): void {
    this.dialogService.openDialog(PaymentsEntityComponent, 'paymentFormWithData', {
      data: {
        EntityID: null,
        eckType: 'payment',
        bundle: 'payment',
        action: 'create'
      }
    });
  }

  private openNewLesson(): void {
    this.dialogService.openDialog(EventLessonEntityComponent, 'defaultWithData', {
      data: {
        EntityID: null,
        eckType: 'events',
        bundle: 'lessons',
        action: 'create',
        fieldsData: ''
      }
    });
  }

  private showPaymentContextMenu(command: Command): void {
    const paymentActions: Array<EntityContextMenuItem & { hidden?: boolean }> = [
      {
        label: 'View Receipt',
        icon: 'receipt',
        action: 'receipt',
        hidden: !this.permissionsService.canAccess('payments_view')
      },
      {
        label: 'Edit',
        icon: 'edit',
        action: 'edit',
        hidden: !this.permissionsService.canAccess('payments_collect')
      },
      {
        label: 'Delete',
        icon: 'delete',
        action: 'delete',
        hidden: !this.permissionsService.canAccess('payments_delete')
      },
      {
        label: 'Refund',
        icon: 'undo',
        action: 'refund',
        hidden: !this.permissionsService.canAccess('payments_refund')
      }
    ];

    const contextMenuData: EntityContextMenuData = {
      entityId: command.id,
      entityType: 'payment',
      entityTitle: command.label || 'Payment #' + command.id,
      actions: paymentActions.filter(action => !action.hidden)
    };

    const contextMenuRef = this.dialogService.openDialog(EntityContextMenuDialogComponent, 'defaultWithData', contextMenuData);

    contextMenuRef.afterClosed().subscribe(action => {
      if (action) {
        this.handlePaymentAction(action, command.id);
      }
    });
  }

  private handlePaymentAction(action: string, paymentId: string): void {
    switch (action) {
      case 'receipt':
        this.dialog.open(PaymentReceiptComponent, {
          width: '800px',
          height: '800px',
          data: { paymentId: paymentId },
          panelClass: 'receipt-dialog',
          disableClose: false
        });
        this.close();
        break;

      case 'edit':
        this.dialogService.openDialog(PaymentsEntityComponent, 'paymentFormWithData', {
          data: {
            EntityID: paymentId,
            eckType: 'payments',
            bundle: 'payment',
            action: 'edit'
          }
        });
        this.close();
        break;

      case 'delete':
        this.dialogService.openDialog(PaymentsEntityComponent, 'paymentFormWithData', {
          data: {
            EntityID: paymentId,
            eckType: 'payments',
            bundle: 'payment',
            action: 'delete'
          }
        });
        this.close();
        break;

      case 'refund':
        this.dialogService.openDialog(PaymentsEntityComponent, 'paymentFormWithData', {
          data: {
            EntityID: paymentId,
            eckType: 'payments',
            bundle: 'payment',
            action: 'refund'
          }
        });
        this.close();
        break;
    }
  }

  private showEnrollmentContextMenu(command: Command): void {
    const enrollmentActions: Array<EntityContextMenuItem & { hidden?: boolean }> = [
      { label: 'View', icon: 'visibility', action: 'view', hidden: !this.permissionsService.canAccess('enrollments_view') },
      { label: 'Edit', icon: 'edit', action: 'edit', hidden: !this.permissionsService.canAccess('enrollments_edit') },
      { label: 'Sign', icon: 'how_to_reg', action: 'sign', hidden: !this.permissionsService.canAccess('enrollments_edit') },
      { label: 'Delete', icon: 'delete', action: 'delete', hidden: !this.permissionsService.canAccess('enrollments_cancel') },
      { label: 'Drop', icon: 'flight_takeoff', action: 'drop', hidden: !this.permissionsService.canAccess('enrollments_cancel') },
      { label: 'Refund', icon: 'undo', action: 'refund', hidden: !this.permissionsService.canAccess('payments_refund') },
      { label: 'View Lessons', icon: 'menu_book', action: 'lessons', hidden: !this.permissionsService.canAccess('enrollments_view') },
      { label: 'View Payments', icon: 'payment', action: 'payments', hidden: !this.permissionsService.canAccess('enrollments_view') },
      { label: 'View Payments Due', icon: 'pending_actions', action: 'payments-due', hidden: !this.permissionsService.canAccess('enrollments_view') },
      { label: 'Add Payment', icon: 'add_card', action: 'add-payment', hidden: !this.permissionsService.canAccess('payments_collect') },
    ];

    const contextMenuData: EntityContextMenuData = {
      entityId: command.id,
      entityType: 'enrollment',
      entityTitle: command.label || 'Enrollment #' + command.id,
      actions: enrollmentActions.filter(action => !action.hidden)
    };

    const contextMenuRef = this.dialogService.openDialog(EntityContextMenuDialogComponent, 'defaultWithData', contextMenuData);

    contextMenuRef.afterClosed().subscribe(action => {
      if (action) {
        this.handleEnrollmentAction(action, command.id, command.contact_id);
      }
    });
  }

  private handleEnrollmentAction(action: string, enrollmentId: string, contactId?: string): void {
    switch (action) {
      case 'view':
        this.dialogService.openDialog(EnrollmentEntityComponent, 'enrollmentFormWithData', {
          data: {
            EntityID: enrollmentId,
            eckType: 'packages',
            bundle: 'enrollment',
            action: 'view'
          }
        });
        break;

      case 'edit':
        this.dialogService.openDialog(EnrollmentEntityComponent, 'enrollmentFormWithData', {
          data: {
            EntityID: enrollmentId,
            eckType: 'packages',
            bundle: 'enrollment',
            action: 'editEnrollment'
          }
        });
        break;

      case 'sign':
        this.dialogService.openDialog(SignatureEnrollmentComponent, 'enrollmentFormWithData', {
          data: {
            EntityID: enrollmentId,
            eckType: 'packages',
            bundle: 'enrollment',
            action: 'signEnrollment'
          }
        });
        break;

      case 'delete':
        this.dialogService.openDialog(EnrollmentEntityComponent, 'enrollmentFormWithData', {
          data: {
            EntityID: enrollmentId,
            eckType: 'packages',
            bundle: 'enrollment',
            action: 'delete'
          }
        });
        break;

      case 'drop':
        this.dialogService.openDialog(EnrollmentDropComponent, 'defaultWithData', {
          data: {
            EntityID: enrollmentId,
            eckType: 'packages',
            bundle: 'enrollment',
            action: 'drop'
          }
        });
        break;

      case 'refund':
        this.dialogService.openDialog(PaymentsEntityComponent, 'paymentFormWithData', {
          data: {
            EntityID: null,
            eckType: 'payments',
            bundle: 'payment',
            action: 'refund',
            fieldsData: {
              field_gross_tuition: '',
              field_enrollment_name: { target_id: enrollmentId },
              field_student_name: contactId,
              field_payment_type: '71'
            }
          }
        });
        break;

      case 'add-payment':
        this.dialogService.openDialog(PaymentsEntityComponent, 'paymentFormWithData', {
          data: {
            EntityID: null,
            eckType: 'payments',
            bundle: 'payment',
            action: 'create',
            fieldsData: {
              field_enrollment_name: [{ target_id: enrollmentId, payment_amount: 0 }],
              field_student_name: contactId
            }
          }
        });
        break;

      case 'lessons':
        this.dialogService.openDialog(EnrollmentViewLessonComponent, 'defaultWithData', {
          data: {
            EntityID: enrollmentId,
            eckType: 'packages',
            bundle: 'enrollment',
            action: '0'
          }
        });
        break;

      case 'payments':
        this.dialogService.openDialog(ViewPaymentsFromEnrollmentComponent, 'defaultWithData', {
          data: {
            EntityID: enrollmentId,
            eckType: 'packages',
            bundle: 'enrollment'
          }
        });
        break;

      case 'payments-due':
        this.dialogService.openDialog(ViewPaymentsDueFromEnrollmentComponent, 'defaultWithData', {
          data: {
            EntityID: enrollmentId,
            eckType: 'packages',
            bundle: 'enrollment'
          }
        });
        break;
    }

    this.close();
  }

  private getConfigurationCommands(): Command[] {
    return [
      {
        id: 'config-studio-info',
        label: 'Studio Information Settings',
        icon: 'info',
        route: '/configuration/studio-information',
        permission: 'config_studio',
        keywords: ['settings', 'studio', 'configuration', 'information']
      },
      {
        id: 'config-tax',
        label: 'Tax Configuration',
        icon: 'account_balance',
        route: '/configuration/tax-configuration',
        permission: 'config_studio',
        keywords: ['settings', 'tax', 'configuration']
      },
      {
        id: 'config-calendar',
        label: 'Calendar Configuration',
        icon: 'calendar_month',
        route: '/configuration/calendar-configuration',
        permission: 'config_studio',
        keywords: ['settings', 'calendar', 'configuration', 'working hours', 'session start times']
      },
      {
        id: 'config-staff-management',
        label: 'Staff Management Settings',
        icon: 'people',
        route: '/configuration/staff-management',
        permission: 'staff_view',
        keywords: ['settings', 'staff', 'management']
      },
      {
        id: 'config-gift-certificates',
        label: 'Gift Certificates Settings',
        icon: 'card_giftcard',
        route: '/configuration/gift-certificates',
        permission: 'config_studio',
        keywords: ['settings', 'gift', 'certificates']
      },
      {
        id: 'config-email-templates',
        label: 'Email Template Settings',
        icon: 'email',
        route: '/configuration/email-template-settings',
        permission: 'config_studio',
        keywords: ['settings', 'email', 'templates', 'communication']
      },
      {
        id: 'config-sms-templates',
        label: 'SMS Template Settings',
        icon: 'sms',
        route: '/configuration/sms-template-settings',
        permission: 'config_studio',
        keywords: ['settings', 'sms', 'templates', 'communication']
      },
      {
        id: 'config-stripe',
        label: 'Stripe Integration Settings',
        icon: 'credit_card',
        route: '/configuration/stripe-integration',
        permission: 'config_integrations',
        keywords: ['settings', 'stripe', 'integration', 'payments']
      },
      {
        id: 'config-square',
        label: 'Square Integration Settings',
        icon: 'crop_square',
        route: '/configuration/square-integration',
        permission: 'config_integrations',
        keywords: ['settings', 'square', 'integration', 'payments']
      },
      {
        id: 'config-paybright',
        label: 'PayBright Integration Settings',
        icon: 'point_of_sale',
        route: '/configuration/paybright-integration',
        permission: 'config_integrations',
        keywords: ['settings', 'paybright', 'integration', 'payments']
      },
      {
        id: 'config-twilio',
        label: 'Twilio Integration Settings',
        icon: 'phone',
        route: '/configuration/stripe-twilio',
        permission: 'config_integrations',
        keywords: ['settings', 'twilio', 'integration', 'communication']
      }
    ];
  }

  private getMenuCommands(): Command[] {
    const commands: Command[] = [];

    // Get menu items from MenuRESTService
    const menuItems = this.menuRESTService.loadedMenuItems || [];

    menuItems.forEach((item: any) => {
      // Process items with nested "below" structure (like Reports)
      if (item.below && Array.isArray(item.below)) {
        item.below.forEach((subItem: any) => {
          if (subItem.relative && subItem.title) {
            // Determine icon based on parent or title
            let icon = 'description';
            if (item.title === 'Reports') {
              icon = 'assessment';
            } else if (item.title === 'Appointments') {
              icon = 'event';
            } else if (item.title === 'Students') {
              icon = 'people';
            }

            commands.push({
              id: `menu-${item.title.toLowerCase()}-${subItem.title.toLowerCase().replace(/\s+/g, '-')}`,
              label: `${item.title}: ${subItem.title}`,
              icon: icon,
              route: subItem.relative,
              keywords: [item.title.toLowerCase(), subItem.title.toLowerCase()]
            });
          }
        });
      }
    });

    return commands;
  }

  private rebuildCommands(): void {
    this.commands = this.filterVisibleCommands([
      ...this.staticCommands,
      ...this.getConfigurationCommands(),
      ...this.getMenuCommands()
    ]);

    if (!this.searchQuery.trim()) {
      this.filteredCommands = [...this.commands];
      return;
    }

    this.onSearchChange();
  }

  private filterVisibleCommands(commands: Command[]): Command[] {
    return commands.filter(command => this.isCommandVisible(command));
  }

  private isCommandVisible(command: Command): boolean {
    if (command.permission && !this.permissionsService.canAccess(command.permission)) {
      return false;
    }

    if (command.isVisible) {
      return command.isVisible();
    }

    return true;
  }

  private canAccessAny(permissions: string[]): boolean {
    return permissions.some(permission => this.permissionsService.canAccess(permission));
  }

  private canAccessEntityResult(command: Command): boolean {
    switch (command.type) {
      case 'student':
        return this.permissionsService.canAccess('students_view');
      case 'payment':
        return this.permissionsService.canAccess('payments_view');
      case 'enrollment':
        return this.permissionsService.canAccess('enrollments_view');
      case 'lesson':
        return this.permissionsService.canAccess('lessons_edit');
      default:
        return true;
    }
  }
}
